import React, { useState } from 'react';
import { MessageCircle, X, Send, Bot } from 'lucide-react';

interface Message {
  id: string;
  text: string;
  isBot: boolean;
  timestamp: Date;
}

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Welcome to our perfume market platform AKIDO™! I\'m your AI assistant here to help you find the perfect fragrance. How can I assist you today?',
      isBot: true,
      timestamp: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState('');

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputText,
      isBot: false,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');

    // Simulate bot response
    setTimeout(() => {
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: getBotResponse(inputText),
        isBot: true,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, botResponse]);
    }, 1000);
  };

  const getBotResponse = (userInput: string): string => {
    const input = userInput.toLowerCase();
    
    if (input.includes('hello') || input.includes('hi')) {
      return 'Hello! Welcome to AKIDO™. I\'m here to help you discover amazing fragrances. What type of scent are you looking for today?';
    } else if (input.includes('r')) {
      return 'We have beautiful rose fragrances! Our Midnight Rose Elegance and Royal Rose Oud are very popular. Would you like to know more about them?';
    } else if (input.includes('daffodil')) {
      return 'Our daffodil collection is fresh and vibrant! Golden Daffodil Dreams and Spring Daffodil Breeze are customer favorites.';
    } else if (input.includes('aster')) {
      return 'Aster Mystique is our premium mysterious fragrance with deep amber notes. It\'s perfect for evening wear!';
    } else if (input.includes('price') || input.includes('cost')) {
      return 'Our perfumes range from ₹6,999 to ₹19,999. We often have sales with great discounts. Would you like to see our current offers?';
    } else if (input.includes('delivery') || input.includes('shipping')) {
      return 'We offer free delivery across India for orders above ₹5,000. Express delivery is available for same-day delivery in major cities.';
    } else if (input.includes('return') || input.includes('exchange')) {
      return 'We have a 7-day return policy for unopened products. If you\'re not satisfied, we\'ll provide a full refund or exchange.';
    } else {
      return 'Thank you for your question! I\'m here to help you find the perfect fragrance at AKIDO™. You can browse our collection by categories like Rose, Daffodil, Aster, or general Flower fragrances. Is there anything specific you\'d like to know?';
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {isOpen && (
        <div className="bg-gradient-to-br from-gray-900 to-black border border-gold-500/30 rounded-xl shadow-2xl w-80 h-96 mb-4 flex flex-col">
          <div className="bg-gradient-to-r from-gold-500 to-gold-600 p-4 rounded-t-xl flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Bot className="w-5 h-5 text-black" />
              <span className="text-black font-semibold">AKIDO™ Assistant</span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-black hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.isBot ? 'justify-start' : 'justify-end'}`}
              >
                <div
                  className={`max-w-xs p-3 rounded-lg ${
                    message.isBot
                      ? 'bg-gray-800 text-gray-200'
                      : 'bg-gold-500 text-black'
                  }`}
                >
                  <p className="text-sm">{message.text}</p>
                  <p className="text-xs mt-1 opacity-70">
                    {message.timestamp.toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-700">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500"
              />
              <button
                type="submit"
                className="bg-gold-500 hover:bg-gold-600 text-black p-2 rounded-lg transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>
      )}

      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700 text-black p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 animate-bounce-subtle"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
}